import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router';
import * as Auth from './Screens/Authentication/AuthRoutes';
import { ConnectScreen } from './Screens/Connect';
import { DashboardLayout } from './Screens/Dashboard';
import { FourOhFour } from './Screens/Dashboard';
import { OnboardingScreen } from './Screens/Onboarding';
import { getFlatMenuLinks } from './menu-links';
import { useAuth } from 'hooks/useAuth';
import { useOnboarding } from '@/state/onboarding/useOnboarding';

type SignedInAppProps = { flatLinks: ReturnType<typeof getFlatMenuLinks> };

/** An app connecting over OAuth (PER-334) sends the browser here: sign in first, then come back. */
const SignInToConnect = () => {
  const { uid = '' } = useParams();
  return <Navigate to={`/auth/signin?from=${encodeURIComponent(`/connect/${uid}`)}`} replace />;
};

/**
 * A signed-in account that still has setting up to do sees onboarding and nothing
 * else (no sidebar, no empty dashboards). If the question cannot be answered
 * (the request failed) the dashboard is shown: a failed lookup must not lock
 * anyone out of their own account.
 */
const SignedInApp = ({ flatLinks }: SignedInAppProps) => {
  const { onboarding, isLoading } = useOnboarding();

  if (isLoading) return null;

  if (onboarding?.required) {
    return (
      <Routes>
        <Route path="/auth/signout" element={<Auth.SignoutScreen />} />
        <Route path="/connect/:uid" element={<ConnectScreen />} />
        <Route path="*" element={<OnboardingScreen onboarding={onboarding} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* The OAuth consent screen stands alone: no sidebar, nothing else to click away to. */}
      <Route path="/connect/:uid" element={<ConnectScreen />} />
      <Route
        path="*"
        element={
          <DashboardLayout>
            <Routes>
              {flatLinks.map((link) => (
                <Route key={link.key} path={link.url} element={link.element} />
              ))}
              <Route path="/auth/signout" element={<Auth.SignoutScreen />} />
              <Route path="/auth/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<FourOhFour />} />
            </Routes>
          </DashboardLayout>
        }
      />
    </Routes>
  );
};

export function App() {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) return null;

  const flatLinks = getFlatMenuLinks({ user });

  return (
    <BrowserRouter>
      {!isAuthenticated && (
        <Routes>
          <Route path="/auth/signin" element={<Auth.SigninScreen />} />
          <Route path="/auth/signup" element={<Auth.SignupScreen />} />
          <Route path="/auth/verify" element={<Auth.VerifyScreen />} />
          <Route path="/auth/accept-invitation" element={<Auth.AcceptInvitation />} />
          <Route path="/auth/reset-password" element={<Auth.ResetPasswordScreen />} />
          <Route path="/auth/forgot-password" element={<Auth.ForgotPasswordScreen />} />
          <Route path="/connect/:uid" element={<SignInToConnect />} />
          <Route path="*" element={<Navigate to="/auth/signin" replace />} />
        </Routes>
      )}
      {isAuthenticated && <SignedInApp flatLinks={flatLinks} />}
    </BrowserRouter>
  );
}
